import { provideHttpClient, withInterceptors, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import {
  ApplicationConfig,
  EnvironmentProviders,
  importProvidersFrom,
  inject,
  makeEnvironmentProviders,
  provideEnvironmentInitializer,
  provideZoneChangeDetection
} from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer, Title } from '@angular/platform-browser';
import { provideRouter, withEnabledBlockingInitialNavigation } from '@angular/router';
import { provideAuthModule } from '@iot-platform/auth';
import {
  ApiInterceptorModule,
  CustomRouterSerializer,
  GoogleDriveService,
  LocalStorageModule,
  provideCoreTrackingProviders,
  provideDefaultProvidersToken,
  provideIotHighcharts,
  provideIotNativeDateAdapter,
  provideIotPlatformIndexedDb
} from '@iot-platform/core';
import { provideIotPlatformApplicationState, provideIotPlatformBusinessProfileWhenReadRuleOnDeviceConfiguratorConceptState } from '@iot-platform/data-access';
import { DeviceCommandService, provideDeviceCommandSettings, provideDeviceConnectivityModuleSettings } from '@iot-platform/data-access/devices';
import { FavoriteViewsModule } from '@iot-platform/data-access/favorite-views';
import { PreferencesModule, UserPreferencesFavoriteFilterEngineService } from '@iot-platform/data-access/users';
import { I18nModule } from '@iot-platform/i18n';
import { FilterEngineV2Module, provideFFlowSettings } from '@iot-platform/iot-platform-ui';
import { AppName } from '@iot-platform/models/common';
import { provideNteAuditTrailSettings, trackingSettings, TrackingSettingsProviderService } from '@iot-platform/nte/util';
import { DeviceConnectivityModuleService } from '@iot-platform/nte/data-access/devices';
import { EffectsModule } from '@ngrx/effects';
import { DEFAULT_ROUTER_FEATURENAME, provideRouterStore, routerReducer } from '@ngrx/router-store';
import { ActionReducer, MetaReducer, provideStore, StoreModule } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { LicenseManager as AgGridLicenseManager } from 'ag-grid-enterprise';
import { environment } from '../environments/environment';
import { APP_ROUTES } from './app.routes';

export function provideNteApplicationState(): EnvironmentProviders[] {
  return [].concat(provideIotPlatformApplicationState(), provideIotPlatformBusinessProfileWhenReadRuleOnDeviceConfiguratorConceptState());
}

function debugState(reducer: ActionReducer<unknown>): ActionReducer<unknown> {
  return function (state, action) {
    console.log(action, state);
    return reducer(state, action);
  };
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const metaReducers: MetaReducer<unknown>[] = environment.production ? [] : [debugState];

const storeSettings = {
  // metaReducers,
  runtimeChecks: {
    strictStateImmutability: false,
    strictActionImmutability: false,
    strictStateSerializability: false,
    strictActionSerializability: false,
    strictActionTypeUniqueness: false
  }
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideIotHighcharts(),
    provideDefaultProvidersToken({ environment }),
    provideRouter(APP_ROUTES, withEnabledBlockingInitialNavigation()),
    provideHttpClient(withXhr(), withInterceptorsFromDi(), withInterceptors([])),
    provideApplicationInitializer(),
    provideIotNativeDateAdapter(),
    provideStore(
      {
        [DEFAULT_ROUTER_FEATURENAME]: routerReducer
      },
      storeSettings
    ),
    provideRouterStore({ serializer: CustomRouterSerializer }),
    provideStoreDevtools({ maxAge: 30, logOnly: environment.production }),
    provideAuthModule({
      ssoDisabled: false,
      appName: AppName.NTE
    }),
    importProvidersFrom(
      StoreModule.forRoot({}, storeSettings),
      EffectsModule.forRoot([]),
      FavoriteViewsModule,
      I18nModule.forRoot({ langs: ['fr', 'en', 'de'] }),
      LocalStorageModule.forRoot({ prefix: environment.storage.appPrefix }),
      PreferencesModule,
      ApiInterceptorModule.forRoot({
        excludedApis: ['/admin-profiles', '/v2undefined', '/v1/tag-categories', '/entities/undefined/admin-profiles'],
        replaceApis: [
          {
            endpoint: `${environment.api.url}/nte-devices`,
            replaceBy: `${environment.api.url}${environment.api.endpoints.devices}`
          }
        ]
      }),
      FilterEngineV2Module.withSettings({
        userPreferencesService: UserPreferencesFavoriteFilterEngineService
      })
    ),
    provideCoreTrackingProviders(trackingSettings, TrackingSettingsProviderService),
    provideNteAuditTrailSettings(),
    provideDeviceCommandSettings({ deviceCommandService: DeviceCommandService }),
    provideDeviceConnectivityModuleSettings({ deviceConnectivityModuleService: DeviceConnectivityModuleService }),
    provideIotPlatformIndexedDb(),
    provideFFlowSettings()
  ]
};

function provideApplicationInitializer(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideEnvironmentInitializer(() => {
      AgGridLicenseManager.setLicenseKey(environment.apiKeys.agGrid);
      inject(GoogleDriveService).loadGoogleLibraries();
      inject(MatIconRegistry).addSvgIconSet(inject(DomSanitizer).bypassSecurityTrustResourceUrl('assets/svg/iot4bos-icon-set.svg'));
      inject(MatIconRegistry).addSvgIconSet(inject(DomSanitizer).bypassSecurityTrustResourceUrl('assets/svg/xmqtt-icon-set.svg'));
      if (environment.envType !== 'prod') {
        const link: HTMLLinkElement | null = document.querySelector('link[rel="icon"]');
        if (link) {
          link.href = `/assets/favicons/favicon-${environment.envType}.ico`;
        }
        const titleService = inject(Title);
        titleService.setTitle(`${titleService.getTitle()} - ${environment.envName}`);
      }
    })
  ]);
}
